import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { GapRow } from '../../ui/Gaps'
import {
  formatAmount,
  formatDate,
  formatDayTime,
  formatMoney,
  formatPhone,
  nowMs,
} from '@dorixona/utils'
import { isApiError } from '@dorixona/api'
import { Icon } from '../../ui/Icon'
import { DetailHead } from '../../ui/DetailHead'
import { Failed, Loading, failureText } from '../../ui/Load'
import { MapView } from '../../ui/MapView'
import { Modal } from '../../ui/Modal'
import { Pagination } from '../../ui/Pagination'
import { TableSkeleton } from '../../ui/Skeleton'
import { useSession } from '../auth/session'
import { useStaffPage } from '../admins/api'
import { useDashboard } from '../dashboard/api'
import { dashRange, rangeQuery } from '../dashboard/model'
import { useOrders, type Order } from '../orders/api'
import { STATUS_TONE, shipText, statusText } from '../orders/model'
import { useDeleteStore, useSaveStore, useStoreStock, useStores, type Store } from './api'
import { ALL_DAY, STORE_IN_USE, hoursText, storeTitle, weekRows } from './model'
import { StoreFom } from './StoreFom'

export interface StoreScreenProps {
  onToast: (message: string) => void
}

type Tab = 'over' | 'orders' | 'staff' | 'stock' | 'hours' | 'fom'

const PAGE_SIZES = [10, 25, 50] as const

/** Показатели аптеки — за последние 30 дней, как на дашборде. */
const STATS_DAYS = 30

/** Способ получения — квадратной меткой с иконкой, как в списке заказов. */
function Ship({ order }: { order: Order }) {
  return (
    <span className="ui-method" data-method={order.ship}>
      <Icon name={order.ship === 'delivery' ? 'i-truck' : 'i-pin'} size={14} />
      {shipText(order.ship)}
    </span>
  )
}

function Status({ order }: { order: Order }) {
  return (
    <span className="ui-status" data-tone={STATUS_TONE[order.status]}>
      {statusText(order.status)}
    </span>
  )
}

/**
 * Карточка аптеки: показатели, заказы, кто работает и когда открыто.
 *
 * Числа считает сервер: выручку, средний чек и заказы по способам — дашборд
 * по одной аптеке, «Заказов всего» — `total` списка. Списки листаются
 * страницами; большой страницы ради подсчёта на экране не запрашиваем.
 *
 * Править здесь можно только одно — работает ли аптека: переключатель в шапке
 * сохраняет сразу.
 */
export function StoreScreen({ onToast }: StoreScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const [tab, setTab] = useState<Tab>('over')
  const [deleting, setDeleting] = useState(false)
  const id = Number(params.id)

  const canOrders = can('orders')
  const canStaff = can('admins')
  const stores = useStores()
  const save = useSaveStore()
  const remove = useDeleteStore()
  // Пять последних заказов для «Обзора»; их `total` — число на вкладке
  // «Заказы» и в показателях. Вкладка листает заказы своим запросом.
  const recent = useOrders({ storeId: id, pageSize: 5 }, canOrders && id > 0)
  // Страница сотрудников живёт здесь: её `total` — число на вкладке.
  const [staffPage, setStaffPage] = useState(1)
  const [staffSize, setStaffSize] = useState<number>(PAGE_SIZES[0])
  const staff = useStaffPage(
    { storeId: id, page: staffPage, pageSize: staffSize },
    canStaff && id > 0,
  )

  if (stores.isPending) return <Loading what="аптеку" />
  if (stores.isError) {
    return (
      <Failed
        what="аптеку"
        error={stores.error}
        onRetry={() => {
          void stores.refetch()
        }}
      />
    )
  }

  const store = stores.data.find((s) => s.id === id)
  if (!store) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-pin" />
          <b>Аптека не найдена</b>
          <p>Возможно, её убрали из сети.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate('/stores')
            }}
          >
            К списку аптек
          </button>
        </div>
      </div>
    )
  }

  const canUpdate = can('stores', 'u')
  const hours = hoursText(store)
  // Без права на заказы, сотрудников или интеграции нет и вкладки: сервер ответил бы 403.
  const tabs: [Tab, string, number | undefined, boolean][] = [
    ['over', 'Обзор', undefined, true],
    ['orders', 'Заказы', recent.data?.total, canOrders],
    ['staff', 'Сотрудники', staff.data?.total, canStaff],
    ['stock', 'Остатки', undefined, true],
    ['hours', 'Расписание', undefined, true],
    ['fom', 'FOM', undefined, can('integrations')],
  ]
  const shownTabs = tabs.filter(([, , , shown]) => shown)

  const toggleActive = (active: boolean) => {
    save.mutate(
      { ...store, active },
      {
        onSuccess: () => {
          onToast(
            active
              ? `«${storeTitle(store)}» снова работает`
              : `«${storeTitle(store)}» закрыта: самовывоз и доставка из неё недоступны`,
          )
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  const removeStore = () => {
    remove.mutate(id, {
      onSuccess: () => {
        onToast(`«${storeTitle(store)}» удалена`)
        void navigate('/stores')
      },
      onError: (error) => {
        onToast(isApiError(error) && error.code === 'in_use' ? STORE_IN_USE : failureText(error))
      },
      onSettled: () => {
        setDeleting(false)
      },
    })
  }

  return (
    <>
      <DetailHead
        backLabel="Аптеки"
        onBack={() => {
          void navigate('/stores')
        }}
        title={storeTitle(store)}
        sub={
          <>
            {store.district ? (
              <>
                <span>{store.district}</span>
                <span className="dot" aria-hidden="true" />
              </>
            ) : null}
            <span>{store.address}</span>
            <span className="dot" aria-hidden="true" />
            {hours === ALL_DAY ? (
              <span className="ui-tag">24/7</span>
            ) : (
              <span className="num">{hours}</span>
            )}
            {store.phone ? (
              <>
                <span className="dot" aria-hidden="true" />
                <span className="num">{formatPhone(store.phone)}</span>
              </>
            ) : null}
            {/* Открыта ли сейчас — меняется в течение дня, поэтому статус,
                а способы получения — свойства аптеки, поэтому метки. */}
            {store.openNow ? (
              <span className="ui-status" data-tone="success">
                открыта сейчас
              </span>
            ) : (
              <span className="ui-status">закрыта сейчас</span>
            )}
            {store.acceptsPickup ? <span className="ui-tag">самовывоз</span> : null}
            {store.acceptsDelivery ? <span className="ui-tag">доставка</span> : null}
            {store.dispensesRx ? (
              <span className="ui-tag" data-tone="rx">
                рецепт
              </span>
            ) : null}
          </>
        }
        actions={
          canUpdate ? (
            <>
              <label className="ui-switch st-active">
                <input
                  type="checkbox"
                  role="switch"
                  checked={store.active}
                  onChange={(e) => {
                    toggleActive(e.target.checked)
                  }}
                />
                Аптека работает
              </label>
              <button
                type="button"
                className="ui-btn"
                data-variant="secondary"
                onClick={() => {
                  void navigate(`/stores/${store.id}/edit`)
                }}
              >
                <Icon name="i-edit" />
                Изменить
              </button>
              {can('stores', 'd') ? (
                <button
                  type="button"
                  className="ui-iconbtn ui-danger-icon"
                  aria-label="Удалить аптеку"
                  data-tip="Удалить аптеку"
                  data-tip-pos="bottom"
                  data-tip-align="end"
                  onClick={() => {
                    setDeleting(true)
                  }}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </>
          ) : (
            <>
              {store.active ? (
                <span className="ui-status" data-tone="success">
                  работает
                </span>
              ) : (
                // «закрыта» уже занято часами работы — выключенная аптека
                // подписана иначе, как в списке аптек.
                <span className="ui-status">отключена</span>
              )}
              <span className="ui-tag">только просмотр</span>
            </>
          )
        }
      />

      <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы карточки">
        {shownTabs.map(([key, name, count]) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key)
            }}
          >
            {name}
            {count === undefined ? null : <span className="ui-count">{count}</span>}
          </button>
        ))}
      </div>

      {tab === 'over' ? (
        <div className="ui-stack">
          {canOrders || can('dashboard') ? (
            <StoreStats id={id} recent={recent} canOrders={canOrders} canDash={can('dashboard')} />
          ) : null}
          <div className={canOrders ? 'ui-detail-grid' : 'st-solo'}>
            {canOrders ? (
              <RecentOrders
                orders={recent}
                onAll={() => {
                  setTab('orders')
                }}
              />
            ) : null}
            <div className="ui-detail-col">
              <About store={store} />
              <StoreMap store={store} />
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'orders' ? <StoreOrders id={id} /> : null}

      {tab === 'staff' ? (
        <StoreStaff
          staff={staff}
          page={staffPage}
          size={staffSize}
          onPage={setStaffPage}
          onSize={(next) => {
            setStaffSize(next)
            setStaffPage(1)
          }}
        />
      ) : null}

      {tab === 'stock' ? <StoreStock id={id} /> : null}

      {tab === 'hours' ? (
        <section className="ui-card st-solo" aria-labelledby="st-hours">
          <div className="ui-card-head">
            <h3 id="st-hours">Расписание</h3>
            <span className="aside">
              <Icon name="i-eye" />
              видно в приложении
            </span>
          </div>
          <div className="ui-card-body st-facts-body">
            <div className="ui-facts">
              {weekRows(store).map((row) => (
                <div key={row.day}>
                  <span className="k">{row.day}</span>
                  <span className="v num">{row.hours}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {tab === 'fom' ? <StoreFom store={store} onToast={onToast} /> : null}

      {deleting ? (
        <Modal
          title="Удалить аптеку?"
          onClose={() => {
            setDeleting(false)
          }}
        >
          <p>
            «{storeTitle(store)}» пропадёт из сети: самовывоз и доставка из неё прекратятся. История
            заказов останется.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setDeleting(false)
              }}
            >
              Не удалять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              disabled={remove.isPending}
              aria-busy={remove.isPending}
              onClick={removeStore}
            >
              Удалить аптеку
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  )
}

type OrdersQuery = ReturnType<typeof useOrders>
type StaffQuery = ReturnType<typeof useStaffPage>

interface StoreStatsProps {
  id: number
  recent: OrdersQuery
  canOrders: boolean
  canDash: boolean
}

/**
 * Показатели четырьмя плитками, как у клиента: число крупно, под ним — из
 * чего оно сложено. Выручку, средний чек, заказы по способам и «в работе»
 * считает дашборд по этой аптеке за 30 дней; «Заказов всего» — `total`
 * списка заказов. Пока число идёт — полоска на его месте.
 */
function StoreStats({ id, recent, canOrders, canDash }: StoreStatsProps) {
  // Границы периода — один раз на экран: иначе ключ запроса менялся бы
  // с каждой отрисовкой.
  const [period] = useState(() => rangeQuery(dashRange(nowMs(), STATS_DAYS, null, null)))
  const dash = useDashboard({ ...period, storeId: id }, canDash)
  const totals = dash.data?.totals
  const shown = (query: { isPending: boolean }, value: string | undefined): ReactNode => {
    if (query.isPending) return <span className="ui-skeleton st-skel" />
    return value ?? '—'
  }
  const inPeriod = totals ? totals.ordersPickup + totals.ordersDelivery : undefined

  return (
    <section
      className="ui-tiles"
      aria-label="Показатели аптеки"
      aria-busy={(canOrders && recent.isPending) || (canDash && dash.isPending)}
    >
      {canOrders ? (
        <div className="ui-tile">
          <span className="lbl">Заказов всего</span>
          <span className="val">
            {shown(recent, recent.data ? formatAmount(recent.data.total) : undefined)}
          </span>
          {canDash ? (
            <span className="sub">
              {shown(
                dash,
                inPeriod === undefined ? undefined : `${inPeriod} за ${STATS_DAYS} дней`,
              )}
            </span>
          ) : null}
        </div>
      ) : null}
      {canDash ? (
        <>
          <div className="ui-tile">
            <span className="lbl">Выручка за {STATS_DAYS} дней</span>
            <span className="val">{shown(dash, totals && formatMoney(totals.revenueTotal))}</span>
            <span className="sub">
              {shown(
                dash,
                totals && `самовывоз ${totals.ordersPickup} · доставка ${totals.ordersDelivery}`,
              )}
            </span>
          </div>
          <div className="ui-tile">
            <span className="lbl">Средний чек</span>
            <span className="val">
              {/* Заказов за период не было — среднего чека нет, а не «0 сум». */}
              {shown(
                dash,
                totals && (totals.averageOrder ? formatMoney(totals.averageOrder) : '—'),
              )}
            </span>
            <span className="sub">за {STATS_DAYS} дней</span>
          </div>
          <div className="ui-tile">
            <span className="lbl">В работе</span>
            <span className="val">{shown(dash, totals && formatAmount(totals.ordersInWork))}</span>
            <span className="sub">собирают и выдают сейчас</span>
          </div>
        </>
      ) : null}
      {dash.isError ? (
        <Failed
          what="показатели"
          error={dash.error}
          onRetry={() => {
            void dash.refetch()
          }}
        />
      ) : null}
    </section>
  )
}

/** Остатки аптеки: что лежит на полке по данным FOM. Поиск и страницы — сервер. */
function StoreStock({ id }: { id: number }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(PAGE_SIZES[0])
  const stock = useStoreStock(id, { q, page, pageSize: size }, true)

  return (
    <div className="ui-stack">
      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Название или код FOM"
            aria-label="Поиск по остаткам"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(1)
            }}
          />
        </div>
      </div>
      <section className="ui-table-card" aria-labelledby="st-stock">
        <div className="ui-card-head">
          <h3 id="st-stock">
            Остатки <span className="ui-tag">FOM</span>
            {stock.data ? <span className="ui-count">{stock.data.total}</span> : null}
          </h3>
          <span className="aside">меняются только в FOM</span>
        </div>
        {stock.isPending ? <TableSkeleton what="остатки" /> : null}
        {stock.isError ? (
          <Failed
            what="остатки"
            error={stock.error}
            onRetry={() => {
              void stock.refetch()
            }}
          />
        ) : null}
        {stock.data?.items.length === 0 ? (
          <div className="ui-empty">
            <Icon name="i-box" />
            <b>{q.trim() ? 'Ничего не найдено' : 'На полке пусто'}</b>
          </div>
        ) : null}
        {stock.data && stock.data.items.length > 0 ? (
          <>
            <div className="ui-table-wrap" aria-busy={stock.isPlaceholderData}>
              <table className="ui-table">
                <thead>
                  <tr>
                    <th scope="col">Товар</th>
                    <th scope="col">Код FOM</th>
                    <th scope="col" data-align="end">
                      Остаток, шт
                    </th>
                    <th scope="col" data-align="end">
                      Цена, сум
                    </th>
                    <th scope="col">Обновлено</th>
                  </tr>
                </thead>
                <tbody>
                  {stock.data.items.map((line) => {
                    const href = `/products/${line.productId}`
                    return (
                      <tr
                        key={line.productId}
                        data-href={href}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        <td>
                          <Link className="primary" to={href}>
                            {line.name}
                          </Link>
                        </td>
                        <td>
                          <span className="ui-code">{line.fomCode || '—'}</span>
                        </td>
                        <td className="num" data-align="end">
                          {formatAmount(line.qty)}
                        </td>
                        <td className="num" data-align="end">
                          {formatAmount(line.price)}
                        </td>
                        <td className="num secondary">
                          {line.syncedAt ? formatDayTime(line.syncedAt) : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы остатков"
              page={page}
              pageSize={size}
              total={stock.data.total}
              sizes={PAGE_SIZES}
              busy={stock.isPlaceholderData}
              onPage={setPage}
              onPageSize={(next) => {
                setSize(next)
                setPage(1)
              }}
            />
          </>
        ) : null}
      </section>
    </div>
  )
}

/**
 * Пять последних заказов аптеки; полный список — во вкладке «Заказы».
 * «В работе» сервер одним фильтром не отдаёт (`status` — один статус),
 * а собирать их с частичного списка значило бы показывать неполную картину.
 */
function RecentOrders({ orders, onAll }: { orders: OrdersQuery; onAll: () => void }) {
  const navigate = useNavigate()

  return (
    <section className="ui-table-card" aria-labelledby="st-recent">
      <div className="ui-card-head">
        <h3 id="st-recent">Последние заказы</h3>
        {orders.data?.items.length ? (
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            data-size="sm"
            onClick={onAll}
          >
            Все заказы
          </button>
        ) : null}
      </div>
      {orders.isPending ? <TableSkeleton what="заказы" rows={3} /> : null}
      {orders.isError ? (
        <Failed
          what="заказы"
          error={orders.error}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : null}
      {orders.data?.items.length === 0 ? <NoOrders /> : null}
      {orders.data?.items.length ? (
        <div className="ui-table-wrap">
          <table className="ui-table">
            <thead>
              <tr>
                <th scope="col">Заказ</th>
                <th scope="col">Дата</th>
                <th scope="col">Получение</th>
                <th scope="col">Статус</th>
              </tr>
            </thead>
            <tbody>
              {orders.data.items.map((order) => {
                const href = `/orders/${order.id}`
                return (
                  <tr
                    key={order.id}
                    data-href={href}
                    onClick={() => {
                      void navigate(href)
                    }}
                  >
                    <td>
                      <Link className="primary num" to={href}>
                        № {order.number}
                      </Link>
                    </td>
                    <td className="num">{formatDayTime(order.createdAt)}</td>
                    <td>
                      <Ship order={order} />
                    </td>
                    <td>
                      <Status order={order} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}

/** Заказов нет: одинаково на «Обзоре» и во вкладке. */
function NoOrders() {
  return (
    <div className="ui-empty">
      <Icon name="i-box" />
      <b>Заказов из этой аптеки пока не было</b>
    </div>
  )
}

interface StoreStaffProps {
  staff: StaffQuery
  page: number
  size: number
  onPage: (page: number) => void
  onSize: (size: number) => void
}

/** Сотрудники, закреплённые за аптекой, — страницами, листает сервер. */
function StoreStaff({ staff, page, size, onPage, onSize }: StoreStaffProps) {
  return (
    <section className="ui-table-card" aria-labelledby="st-staff">
      <div className="ui-card-head">
        <h3 id="st-staff">
          Сотрудники аптеки
          {staff.data?.total ? <span className="ui-count">{staff.data.total}</span> : null}
        </h3>
        <span className="aside">
          <Icon name="i-key" />
          видят только заказы этой аптеки
        </span>
      </div>
      {staff.isPending ? <TableSkeleton what="сотрудников" rows={3} /> : null}
      {staff.isError ? (
        <Failed
          what="сотрудников"
          error={staff.error}
          onRetry={() => {
            void staff.refetch()
          }}
        />
      ) : null}
      {staff.data?.items.length === 0 ? (
        <div className="ui-empty">
          <Icon name="i-users" />
          <b>За аптекой никто не закреплён</b>
          <p>Заказы обрабатывают сотрудники сети.</p>
        </div>
      ) : null}
      {staff.data?.items.length ? (
        <>
          <div className="ui-table-wrap" aria-busy={staff.isPlaceholderData}>
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Сотрудник</th>
                  <th scope="col">Роль</th>
                  <th scope="col">Телефон</th>
                  <th scope="col">Последний вход</th>
                  <th scope="col">Доступ</th>
                </tr>
              </thead>
              <tbody>
                {staff.data.items.map((person) => (
                  <tr key={person.id}>
                    <td className="primary">{person.name}</td>
                    <td>{person.roleName}</td>
                    <td className="num">{formatPhone(person.phone)}</td>
                    <td className="num">
                      {person.lastLogin ? formatDayTime(person.lastLogin) : 'не входил'}
                    </td>
                    <td>
                      {person.active ? (
                        <span className="ui-status" data-tone="success">
                          активен
                        </span>
                      ) : (
                        <span className="ui-status">отключён</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            label="Страницы сотрудников"
            page={page}
            pageSize={size}
            total={staff.data.total}
            sizes={PAGE_SIZES}
            busy={staff.isPlaceholderData}
            onPage={onPage}
            onPageSize={onSize}
          />
        </>
      ) : null}
    </section>
  )
}

/** Что известно об аптеке — только чтение. */
function About({ store }: { store: Store }) {
  return (
    <section className="ui-card" aria-labelledby="st-about">
      <div className="ui-card-head">
        <h3 id="st-about">Об аптеке</h3>
      </div>
      <div className="ui-card-body st-facts-body">
        <div className="ui-facts">
          <div>
            <span className="k">Название</span>
            <span className="v">{store.name}</span>
          </div>
          <div>
            <span className="k">Район</span>
            <span className="v">{store.district || '—'}</span>
          </div>
          <div>
            <span className="k">Адрес</span>
            <span className="v">{store.address}</span>
          </div>
          <div>
            <span className="k">Ближайшее метро</span>
            <span className="v">{store.metro || '—'}</span>
          </div>
          <div>
            <span className="k">Телефон</span>
            <span className="v num">{store.phone ? formatPhone(store.phone) : '—'}</span>
          </div>
          <div>
            <span className="k">Заведующий</span>
            <span className="v">{store.manager || '—'}</span>
          </div>
          <div>
            <span className="k">Отпуск по рецепту</span>
            <span className="v">{store.dispensesRx ? 'разрешён' : 'нет лицензии'}</span>
          </div>
          <div>
            <span className="k">Координаты</span>
            <span className="v num">
              {store.lat === null || store.lng === null
                ? 'не заданы'
                : `${store.lat}, ${store.lng}`}
            </span>
          </div>
          <div>
            <span className="k">В системе с</span>
            <span className="v num">{store.createdAt ? formatDate(store.createdAt) : '—'}</span>
          </div>
          <GapRow what="Фото аптеки, площадь и число касс" where="Store в GET /admin/stores/{id}" />
        </div>
      </div>
    </section>
  )
}

/** Все заказы аптеки — страницами, листает сервер. */
function StoreOrders({ id }: { id: number }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [size, setSize] = useState<number>(PAGE_SIZES[0])
  const orders = useOrders({ storeId: id, page, pageSize: size }, true, true)

  return (
    <section className="ui-table-card" aria-labelledby="st-orders">
      <div className="ui-card-head">
        <h3 id="st-orders">
          Заказы аптеки
          {orders.data?.total ? <span className="ui-count">{orders.data.total}</span> : null}
        </h3>
      </div>
      {orders.isPending ? <TableSkeleton what="заказы" rows={3} /> : null}
      {orders.isError ? (
        <Failed
          what="заказы"
          error={orders.error}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : null}
      {orders.data?.items.length === 0 ? <NoOrders /> : null}
      {orders.data && orders.data.items.length > 0 ? (
        <>
          <div className="ui-table-wrap" aria-busy={orders.isPlaceholderData}>
            <table className="ui-table">
              <thead>
                <tr>
                  <th scope="col">Заказ</th>
                  <th scope="col">Дата</th>
                  <th scope="col">Клиент</th>
                  <th scope="col">Получение</th>
                  <th scope="col" data-align="end">
                    Сумма
                  </th>
                  <th scope="col">Статус</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => {
                  const href = `/orders/${order.id}`
                  return (
                    <tr
                      key={order.id}
                      data-href={href}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td>
                        <Link className="primary num" to={href}>
                          № {order.number}
                        </Link>
                      </td>
                      <td className="num">{formatDayTime(order.createdAt)}</td>
                      <td>{order.customerName}</td>
                      <td>
                        <Ship order={order} />
                      </td>
                      <td className="num" data-align="end">
                        {formatMoney(order.total)}
                      </td>
                      <td>
                        <Status order={order} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            label="Страницы заказов"
            page={page}
            pageSize={size}
            total={orders.data.total}
            sizes={PAGE_SIZES}
            busy={orders.isPlaceholderData}
            onPage={setPage}
            onPageSize={(next) => {
              setSize(next)
              setPage(1)
            }}
          />
        </>
      ) : null}
    </section>
  )
}

/** Где аптека на карте. Координат нет — так и говорим: доставка из неё не пойдёт. */
function StoreMap({ store }: { store: Store }) {
  const point = store.lat !== null && store.lng !== null ? { lat: store.lat, lng: store.lng } : null
  return (
    <section className="ui-card" aria-labelledby="st-map">
      <div className="ui-card-head">
        <h3 id="st-map">На карте</h3>
      </div>
      <div className="ui-card-body">
        {point ? (
          <MapView label={`Аптека «${storeTitle(store)}» на карте`} point={point} height={220} />
        ) : (
          <p className="ui-muted-line">Координаты не заданы — укажите их в «Изменить»</p>
        )}
      </div>
    </section>
  )
}
